import { Edit3, Flame, MessageSquare, ShoppingBag, Snowflake, Trash2, User, Zap } from 'lucide-react';
import { channelBadgeOf, type Deal, type StageItem } from './types';

export function ChannelBadge({ deal }: { deal: Deal }) {
  const badge = channelBadgeOf(deal);
  return (
    <span className="channel-badge-mini" style={{ background: badge.bg, color: badge.color }}>
      {badge.label}
    </span>
  );
}

export function TempBadge({ temp }: { temp?: string }) {
  if (temp === 'hot') return <span className="temp-badge hot"><Flame size={12} /> Горячий</span>;
  if (temp === 'warm') return <span className="temp-badge warm"><Zap size={12} /> Теплый</span>;
  if (temp === 'cold') return <span className="temp-badge cold"><Snowflake size={12} /> Холодный</span>;
  return null;
}

interface DealCardProps {
  deal: Deal;
  stages: StageItem[];
  isDragging: boolean;
  onDragStart: (dealId: number) => void;
  onDragEnd: () => void;
  onEdit: (deal: Deal) => void;
  onDelete: (dealId: number) => void;
  onGoToChat: (conversationId?: number | null) => void;
  onStageChange: (dealId: number, stageName: string) => void;
}

export default function DealCard({
  deal,
  stages,
  isDragging,
  onDragStart,
  onDragEnd,
  onEdit,
  onDelete,
  onGoToChat,
  onStageChange,
}: DealCardProps) {
  const handleDragStart = (event: React.DragEvent<HTMLDivElement>) => {
    event.dataTransfer.setData('text/plain', String(deal.id));
    event.dataTransfer.effectAllowed = 'move';
    onDragStart(deal.id);
  };

  const handleEdit = () => onEdit(deal);
  const handleDelete = () => onDelete(deal.id);
  const handleChat = () => onGoToChat(deal.conversationId);
  const handleStageSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
    onStageChange(deal.id, event.target.value);
  };

  const hasDiscount = Boolean(deal.discountPercent && deal.discountPercent > 0);
  const showOriginal = Boolean(deal.originalAmount && deal.originalAmount > deal.amount);

  return (
    <div
      className={`deal-card ${deal.status} ${isDragging ? 'dragging' : ''}`}
      draggable
      onDragStart={handleDragStart}
      onDragEnd={onDragEnd}
    >
      <div className="card-top">
        <span className="deal-id">#{deal.id}</span>
        <div className="card-badges">
          <ChannelBadge deal={deal} />
          <TempBadge temp={deal.conversation?.temp} />
        </div>
      </div>

      <button type="button" className="deal-title" onClick={handleEdit}>
        {deal.title || 'Заявка'}
      </button>

      <div className="deal-client-info">
        <User size={13} />
        <span className="client-name">{deal.conversation?.contactName || 'Клиент не привязан'}</span>
        {deal.conversation?.contactPhone && (
          <span className="client-phone">• {deal.conversation.contactPhone}</span>
        )}
      </div>

      {deal.items && deal.items.length > 0 && (
        <div className="deal-items-snippet">
          <ShoppingBag size={12} />
          <span>{deal.items.map((item) => `${item.name} (${item.qty} шт)`).join(', ')}</span>
        </div>
      )}

      <div className="card-bottom">
        <div className="deal-amount">
          {showOriginal && (
            <span className="deal-amount-original">
              {(deal.originalAmount || 0).toLocaleString('ru-RU')} ₽
            </span>
          )}
          <span>{(deal.amount || 0).toLocaleString('ru-RU')} ₽</span>
          {hasDiscount && <span className="deal-discount-pill">-{deal.discountPercent}%</span>}
        </div>

        <div className="card-quick-actions">
          {deal.conversationId && (
            <button type="button" className="qa-btn chat" onClick={handleChat} title="Перейти в чат">
              <MessageSquare size={14} />
            </button>
          )}
          <button type="button" className="qa-btn edit" onClick={handleEdit} title="Редактировать">
            <Edit3 size={14} />
          </button>
          <button type="button" className="qa-btn delete" onClick={handleDelete} title="Удалить">
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {deal.stockAppliedAt && (
        <div className="deal-stock-note" title="Товары этой сделки списаны со склада">
          Списано со склада
        </div>
      )}

      <div className="card-stage-switcher">
        <select
          value={deal.stage}
          aria-label={`Переместить заявку «${deal.title || `№${deal.id}`}» в другой этап`}
          onChange={handleStageSelect}
        >
          {stages.map((stage) => (
            <option key={stage.id} value={stage.id}>Переместить в: {stage.label}</option>
          ))}
        </select>
      </div>
    </div>
  );
}
